import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import toast from 'react-hot-toast';

const EmailTester = ({ user }) => {
  const [recipient, setRecipient] = useState(user?.email || 'jake.myers@vpgcentral.com');
  const [eventType, setEventType] = useState('password_reset');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const emailTemplates = [
    { value: 'password_reset', label: 'Password Reset' },
    { value: 'welcome_email', label: 'Welcome Email' },
    { value: 'temporary_password_email', label: 'Temporary Password' },
    { value: 'password_expiry_warning_30_days', label: 'Password Expiry - 30 Days' },
    { value: 'password_expiry_warning_14_days', label: 'Password Expiry - 14 Days' },
    { value: 'password_expiry_warning_7_days', label: 'Password Expiry - 7 Days' },
    { value: 'password_expiry_warning_3_days', label: 'Password Expiry - 3 Days' },
    { value: 'password_expiry_warning_1_days', label: 'Password Expiry - 1 Day (URGENT)' }
  ];

  // Update recipient when user context loads
  useEffect(() => {
    if (user?.email) {
      setRecipient(user.email);
    }
  }, [user]);

  const handleSendEmail = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      const payload = {
        user: {
          id: user?.id || 1,
          email: recipient,
          full_name: user?.full_name || user?.first_name || 'Test User'
        },
        reset_url: 'http://localhost:5173/reset-password?token=sample-token',
        valid_hours: 1
      };

      const response = await api.post('/api/dev/send-test-email/', {
        recipient,
        event_type: eventType,
        payload
      });

      setResult({
        success: true,
        message: response.data.message || 'Email sent successfully!'
      });

      toast.success('Test email sent successfully!');
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send email';
      setResult({
        success: false,
        message: errorMessage
      });
      toast.error(`Failed to send email: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email Configuration */}
        <div>
          <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">Email Configuration</h3>
          
          <div className="space-y-4">
            <div>
              <label className="label">
                Recipient Email
              </label>
              <input
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                className="input"
                placeholder="Enter recipient email"
              />
            </div>

            <div>
              <label className="label">
                Email Template
              </label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                className="input"
              >
                {emailTemplates.map(template => (
                  <option key={template.value} value={template.value}>
                    {template.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={handleSendEmail}
              disabled={loading || !recipient}
              className="btn btn-primary btn-lg w-full"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>Send Test Email</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Template Preview */}
        <div>
          <h3 className="mb-4 text-base font-semibold text-gray-900 dark:text-gray-100">Template Info</h3>
          <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
            <div className="mb-2 flex items-center">
              <Mail className="mr-2 h-4 w-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {emailTemplates.find(t => t.value === eventType)?.label || eventType}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This will send a test {eventType.replace(/_/g, ' ')} email to {recipient} 
              with sample data for testing purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Result Display */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={`rounded-lg border px-4 py-3 ${
            result.success 
              ? 'border-success-500/25 bg-success-50 dark:bg-success-500/10' 
              : 'border-danger-500/25 bg-danger-50 dark:bg-danger-500/10'
          }`}
        >
          <div className="flex items-center">
            {result.success ? (
              <CheckCircle className="mr-2 h-4 w-4 text-success-600 dark:text-success-400" />
            ) : (
              <XCircle className="mr-2 h-4 w-4 text-danger-600 dark:text-danger-400" />
            )}
            <span className={`text-sm font-medium ${
              result.success ? 'text-success-800 dark:text-success-400' : 'text-danger-800 dark:text-danger-400'
            }`}>
              {result.success ? 'Success' : 'Error'}
            </span>
          </div>
          <p className={`mt-1 text-sm ${
            result.success ? 'text-success-700 dark:text-success-400' : 'text-danger-700 dark:text-danger-400'
          }`}>
            {result.message}
          </p>
        </motion.div>
      )}

      {/* Guidelines */}
      <div className="rounded-lg border border-gray-200/70 bg-gray-50 p-4 dark:border-white/[0.07] dark:bg-white/[0.03]">
        <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Email Testing Guidelines:</h4>
        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
          <li>• Check that emails are delivered to the correct recipient</li>
          <li>• Verify email content matches the template</li>
          <li>• Test with different email addresses and user types</li>
          <li>• Ensure links in emails are functional and secure</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailTester;
